/**
 * API client and hooks for mobile capture sessions (#383 / MOB-1).
 *
 * This is the foundation for the QR/link flow. Upload ingestion and the
 * dedicated public page are built in the following MOB slices.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { config } from "@/lib/config";
import { api } from "./client";

export type MobileCaptureMode = "NOTE" | "DOCUMENT" | "VOICE";

export type MobileCaptureSessionStatus = "ACTIVE" | "REVOKED" | "EXPIRED" | "EXHAUSTED";

export type MobileCaptureUploadStatus =
  "RECEIVED" | "PROCESSING" | "READY" | "FAILED" | "NEEDS_REVIEW" | "DONE";

export interface MobileCaptureUpload {
  id: string;
  session_id: string;
  mode: MobileCaptureMode;
  status: MobileCaptureUploadStatus;
  client_file_id: string | null;
  link_owner_user_id: string | null;
  uploader_user_id: string | null;
  filename: string | null;
  mime_type: string | null;
  size_bytes: number | null;
  note_id: string | null;
  document_id: string | null;
  attachment_id: string | null;
  classifier_result: Record<string, unknown> | null;
  extraction_summary: string | null;
  error_message: string | null;
  reviewed_at: string | null;
  reviewed_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface MobileCaptureSession {
  id: string;
  status: MobileCaptureSessionStatus;
  client_file_id: string | null;
  client_file_reference: string | null;
  client_file_label: string | null;
  created_by_user_id: string;
  created_by_user_name: string | null;
  created_by_user_email: string | null;
  expires_at: string;
  revoked_at: string | null;
  revoked_by_user_id: string | null;
  revocation_reason: string | null;
  allowed_modes: MobileCaptureMode[];
  max_uploads: number;
  upload_count: number;
  remaining_uploads: number;
  welcome_message: string | null;
  last_opened_at: string | null;
  open_count: number;
  public_url: string | null;
  uploads: MobileCaptureUpload[];
  created_at: string;
  updated_at: string;
}

export interface CreateMobileCaptureSessionPayload {
  client_file_id?: string | null;
  expires_in_minutes?: number;
  max_uploads?: number;
  allowed_modes?: MobileCaptureMode[];
  welcome_message?: string | null;
}

export interface CreateMobileCaptureSessionResponse {
  session: MobileCaptureSession;
  token: string;
  public_url: string;
  qr_payload: string;
}

/** Un dépôt de CETTE session, vu du téléphone (« Captures récentes », v2). */
export interface PublicMobileCaptureRecentUpload {
  filename: string | null;
  mode: MobileCaptureMode;
  status: MobileCaptureUploadStatus;
  created_at: string;
}

export interface PublicMobileCaptureContext {
  session_id: string;
  status: MobileCaptureSessionStatus;
  expires_at: string;
  allowed_modes: MobileCaptureMode[];
  max_uploads: number;
  upload_count: number;
  remaining_uploads: number;
  welcome_message: string | null;
  client_file_id: string | null;
  recent_uploads: PublicMobileCaptureRecentUpload[];
}

export interface PublicMobileCaptureTextNotePayload {
  title?: string | null;
  content: string;
}

export interface PublicMobileCaptureTextNoteResponse {
  note_id: string;
  upload_count: number;
  remaining_uploads: number;
}

export interface PublicMobileCaptureFileInitPayload {
  target: MobileCaptureMode;
  filename: string;
  mime_type: string;
  size_bytes: number;
  title?: string | null;
  transcription_mode?: "DICTATION" | "MEETING";
  meeting_consent_confirmed?: boolean;
  /** Lot G1 — dépôt depuis le bouton d'une pièce demandée (`?piece=<id>`). */
  required_document_id?: string | null;
  /** Lot G6a — verso / page de plus / photo reprise d'un document déjà déposé par ce lien. */
  document_id?: string | null;
}

export interface PublicMobileCaptureFileInitResponse {
  target: MobileCaptureMode;
  note_id: string | null;
  document_id: string | null;
  attachment_id: string;
  upload_url: string;
  upload_method: "PUT";
  upload_headers: Record<string, string>;
}

export interface PublicMobileCaptureFileConfirmPayload {
  target: MobileCaptureMode;
  note_id?: string | null;
  document_id?: string | null;
  attachment_id: string;
  /** Lot G6a — « Reprendre » : le fichier remplace les précédents (archivés). */
  replace?: boolean;
}

export interface PublicMobileCaptureFileConfirmResponse {
  target: MobileCaptureMode;
  note_id: string | null;
  document_id: string | null;
  attachment_id: string;
  upload_count: number;
  remaining_uploads: number;
}

export interface UpdateMobileCaptureUploadStatusPayload {
  status: MobileCaptureUploadStatus;
}

export interface ListMobileCaptureSessionsParams {
  mine?: boolean;
  status?: MobileCaptureSessionStatus;
  limit?: number;
  offset?: number;
}

export interface MobileCaptureSessionsQueryOptions {
  enabled?: boolean;
  refetchInterval?: number;
}

function sessionsQuery(params: ListMobileCaptureSessionsParams = {}) {
  const search = new URLSearchParams();
  if (params.mine !== undefined) search.append("mine", String(params.mine));
  if (params.status !== undefined) search.append("status", params.status);
  if (params.limit !== undefined) search.append("limit", String(params.limit));
  if (params.offset !== undefined) search.append("offset", String(params.offset));
  const qs = search.toString();
  return `/mobile-capture/sessions${qs ? `?${qs}` : ""}`;
}

const publicBase = `${config.apiBaseUrl.replace(/\/$/, "")}${config.apiPrefix}/public/mobile-capture`;

async function publicFetch<T>(
  path: string,
  init?: { method?: string; body?: object | null },
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };
  const body =
    init?.body !== undefined && init?.body !== null ? JSON.stringify(init.body) : undefined;
  const resp = await fetch(`${publicBase}${path}`, {
    method: init?.method ?? "GET",
    headers,
    ...(body !== undefined ? { body } : {}),
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    let detail = `HTTP ${resp.status}`;
    try {
      const data = JSON.parse(text);
      if (data?.detail) detail = formatPublicErrorDetail(data.detail) ?? detail;
    } catch {
      if (text) detail = text;
    }
    throw new Error(detail);
  }
  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}

function formatPublicErrorDetail(detail: unknown): string | null {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        if (item && typeof item === "object" && "msg" in item) {
          return String((item as { msg: unknown }).msg);
        }
        return null;
      })
      .filter((message): message is string => Boolean(message));
    return messages.length > 0 ? messages.join(" · ") : null;
  }
  if (detail && typeof detail === "object" && "msg" in detail) {
    return String((detail as { msg: unknown }).msg);
  }
  return null;
}

export const mobileCaptureApi = {
  createSession: (payload: CreateMobileCaptureSessionPayload) =>
    api.post<CreateMobileCaptureSessionResponse>("/mobile-capture/sessions", payload),
  listSessions: (params: ListMobileCaptureSessionsParams = {}) =>
    api.get<MobileCaptureSession[]>(sessionsQuery(params)),
  revokeSession: (sessionId: string, reason?: string | null) =>
    api.post<MobileCaptureSession>(`/mobile-capture/sessions/${sessionId}/revoke`, {
      reason: reason ?? null,
    }),
  reviewUpload: (uploadId: string) =>
    api.post<MobileCaptureUpload>(`/mobile-capture/uploads/${uploadId}/review`, {}),
  updateUploadStatus: (uploadId: string, payload: UpdateMobileCaptureUploadStatusPayload) =>
    api.patch<MobileCaptureUpload>(`/mobile-capture/uploads/${uploadId}/status`, payload),
  publicContext: (token: string) =>
    publicFetch<PublicMobileCaptureContext>(`/${encodeURIComponent(token)}`),
  createTextNote: (token: string, payload: PublicMobileCaptureTextNotePayload) =>
    publicFetch<PublicMobileCaptureTextNoteResponse>(`/${encodeURIComponent(token)}/text-note`, {
      method: "POST",
      body: payload,
    }),
  initPublicUpload: (token: string, payload: PublicMobileCaptureFileInitPayload) =>
    publicFetch<PublicMobileCaptureFileInitResponse>(`/${encodeURIComponent(token)}/uploads`, {
      method: "POST",
      body: payload,
    }),
  confirmPublicUpload: (token: string, payload: PublicMobileCaptureFileConfirmPayload) =>
    publicFetch<PublicMobileCaptureFileConfirmResponse>(
      `/${encodeURIComponent(token)}/uploads/confirm`,
      {
        method: "POST",
        body: payload,
      },
    ),
};

export const mobileCaptureKeys = {
  all: ["mobile-capture"] as const,
  sessions: (params: ListMobileCaptureSessionsParams = {}) =>
    [...mobileCaptureKeys.all, "sessions", params] as const,
  publicContext: (token: string) => [...mobileCaptureKeys.all, "public", token] as const,
};

export function useMobileCaptureSessions(
  params: ListMobileCaptureSessionsParams = {},
  opts?: MobileCaptureSessionsQueryOptions,
) {
  return useQuery<MobileCaptureSession[]>({
    queryKey: mobileCaptureKeys.sessions(params),
    queryFn: () => mobileCaptureApi.listSessions(params),
    enabled: opts?.enabled ?? true,
    ...(opts?.refetchInterval !== undefined ? { refetchInterval: opts.refetchInterval } : {}),
  });
}

export function useCreateMobileCaptureSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: mobileCaptureApi.createSession,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mobileCaptureKeys.all });
    },
  });
}

export function useRevokeMobileCaptureSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, reason }: { sessionId: string; reason?: string | null }) =>
      mobileCaptureApi.revokeSession(sessionId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mobileCaptureKeys.all });
    },
  });
}

export function useReviewMobileCaptureUpload() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ uploadId }: { uploadId: string }) => mobileCaptureApi.reviewUpload(uploadId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mobileCaptureKeys.all });
    },
  });
}

export function useUpdateMobileCaptureUploadStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ uploadId, status }: { uploadId: string; status: MobileCaptureUploadStatus }) =>
      mobileCaptureApi.updateUploadStatus(uploadId, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mobileCaptureKeys.all });
    },
  });
}

export function usePublicMobileCaptureContext(token: string, opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: mobileCaptureKeys.publicContext(token),
    queryFn: () => mobileCaptureApi.publicContext(token),
    enabled: (opts?.enabled ?? true) && token.length > 0,
  });
}
